import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { Link } from "react-router-dom";
import DropdownButton, { DropdownOption } from "./Dropdown/DropdownButton.tsx";
import "./Dropdown/styles.scss";
import Spinner from "../Spinner/index";

export interface ButtonProps {
  text: string ;
  onClick?: (e?: React.MouseEvent<Element, MouseEvent>) => void;
  allowClickOnDisabled?: boolean;
  color?: "default" | "primary" | "secondary" | "outline" | "disabled";
  iconLeft?: React.ReactElement;
  iconRight?: React.ReactElement;
  to?: string; // For React Router Links
  link?: string; // For external links
  dropdownOptions?: DropdownOption[]; // Array of dropdown options
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
}

export const getButtonStyles = (color: ButtonProps["color"] = "default") => {
  const baseStyles = "font-medium transition-colors duration-200 flex items-center gap-2 w-fit leading-5 hover:no-underline font-poppins text-base focus:outline-none";

  const colorStyles = {
    default: "px-4 rounded-full text-main-purple border-1 border-main-purple hover:bg-main-purple hover:text-white",
    primary: "px-4 rounded-full bg-main-purple text-white hover:shadow-md hover:text-white",
    secondary: "px-4 rounded-full bg-gray-100 text-gray-800 hover:bg-gray-200 hover:text-gray-800",
    outline: "text-sm text-left border-main-purple border-b w-fit pb-1 text-main-link hover:text-main-purple transition-colors cursor-pointer font-medium",
    disabled: "bg-disabled-button border-disabled-button cursor-pointer hover:bg-disabled-button hover:border-disabled-button hover:text-white"
  };

  return `${baseStyles} ${colorStyles[color]}`;
};

const Button = forwardRef<HTMLElement, ButtonProps & { dispatch?: any }>(({
  text,
  link,
  onClick,
  color = "default",
  iconLeft,
  iconRight,
  to,
  dropdownOptions,
  className = "",
  disabled = false,
  allowClickOnDisabled = false,
  isLoading,
  dispatch,
  ...rest
}, ref) => {
  const buttonRef = useRef<HTMLButtonElement | HTMLAnchorElement | null>(null);

  useImperativeHandle(ref, () => buttonRef.current as HTMLElement);

  const baseStyle = {
    height: color !== "outline" ? 44 : "auto"
  };

  const buttonContent = (
    <>
      {iconLeft && iconLeft}
      {
        isLoading
          ? <div><Spinner color="#FFF" size={6} /></div>
          : <span>{text}</span>
      }
      {iconRight && iconRight}
    </>
  );

  const handleClick = (e: React.MouseEvent<Element, MouseEvent>) => {
    if (onClick && (disabled ? allowClickOnDisabled : true)) {
      onClick(e);
    }
  };

  if (dropdownOptions && dropdownOptions.length > 0) {
    return (
      <DropdownButton
        text={text}
        color={color}
        iconLeft={iconLeft}
        iconRight={iconRight}
        dropdownOptions={dropdownOptions}
        disabled={disabled}
        className={className}
        baseStyle={baseStyle}
        isLoading={isLoading}
        {...rest}
      />
    );
  }

  if (to) {
    return (
      <Link
        ref={buttonRef as React.RefObject<HTMLAnchorElement>}
        to={to}
        className={`${getButtonStyles(disabled ? "disabled" : color)} ${className}`}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        style={baseStyle}
        onClick={handleClick}
        {...rest}
      >
        {buttonContent}
      </Link>
    );
  }

  if (link) {
    return (
      <a
        ref={buttonRef as React.RefObject<HTMLAnchorElement>}
        href={link}
        className={`${getButtonStyles(disabled ? "disabled" : color)} ${className}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-disabled={disabled}
        style={baseStyle}
        tabIndex={disabled ? -1 : 0}
        onClick={handleClick}
        {...rest}
      >
        {buttonContent}
      </a>
    );
  }

  return (
    <button
      ref={buttonRef as React.RefObject<HTMLButtonElement>}
      onClick={handleClick}
      className={`${getButtonStyles(disabled ? "disabled" : color)} ${className}`}
      type="button"
      style={baseStyle}
      tabIndex={disabled ? -1 : 0}
      {...rest}
    >
      {buttonContent}
    </button>
  );
});

Button.displayName = "Button";

export default Button;
